import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Loader2, Phone, Mail, Car, User, Sparkles, Send, FileCode2, Unlock, MessageSquareQuote, Bot, Building2, Eraser } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LeadAdfDialog } from "@/components/LeadAdfDialog";
import { WallInsightsPanel } from "@/components/WallInsightsPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_STYLES = {
  new: "border-amber-700/50 bg-amber-950/40 text-amber-400",
  contacted: "border-sky-700/50 bg-sky-950/40 text-sky-400",
  closed: "border-emerald-700/50 bg-emerald-950/40 text-emerald-400",
};

const CRM_STYLES = {
  sent: "border-emerald-700/50 bg-emerald-950/40 text-emerald-400",
  failed: "border-red-700/50 bg-red-950/40 text-red-400",
  pending: "border-slate-700 bg-slate-900 text-slate-400",
  skipped: "border-amber-700/50 bg-amber-950/40 text-amber-400",
};

const SOURCE_LABELS = {
  matcher: "Specialist quiz",
  vehicle_quick: "Text me this car",
  new_arrivals_alert: "New-arrivals alert",
  specialist_request: "Talk to a specialist",
  wall_lower_price: "Lower-price unlock",
  wall_carfax: "CARFAX unlock",
  wall_carfax_request: "CARFAX requested",
  wall_estimate: "Estimate unlock",
  wall_payment: "Payment calc unlock",
  wall_recon: "Recon report unlock",
  wall_time_on_lot: "Time-on-lot unlock",
  wall_acquisition: "Acquisition unlock",
};

const UNLOCK_LABELS = { lower_price: "Unlocked a lower price", carfax: "Unlocked CARFAX", payment: "Unlocked payment calculator", estimate: "Unlocked repair estimate",
  recon: "Unlocked recon report", time_on_lot: "Unlocked time on lot", acquisition: "Unlocked how we got it" };
const money = (n) => (n == null ? "" : `$${Math.round(n).toLocaleString()}`);

export default function Leads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.platform_owner || user?.platform_admin;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adfLead, setAdfLead] = useState(null);
  const [resending, setResending] = useState(null);
  const [hasStores, setHasStores] = useState(false);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");
  useEffect(() => { api.get("/dealer").then(({ data }) => { setStores(data.stores || []); setHasStores((data.stores || []).length > 0); }).catch(() => {}); }, []);
  const shown = storeFilter === "" ? leads : storeFilter === "unassigned" ? leads.filter((l) => !l.store_id && l.vehicle_id) : leads.filter((l) => l.store_id === storeFilter);

  const load = () => {
    setLoading(true);
    api
      .get("/leads")
      .then(({ data }) => setLeads(data))
      .catch(() => toast.error("Failed to load leads"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setStatus = async (lead, status) => {
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try {
      await api.put(`/leads/${lead.id}`, { status });
      toast.success("Lead updated");
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const resend = async (lead) => {
    setResending(lead.id);
    try {
      const { data } = await api.post(`/leads/${lead.id}/crm/resend`);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? data : l)));
      if (data.crm_status === "sent") toast.success("Lead sent to CRM");
      else toast.error(data.crm_error || "CRM delivery failed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResending(null);
    }
  };

  const purge = async (lead) => {
    if (!window.confirm(`Delete ${lead.customer_name}'s contact details? This scrubs name, phone, email and comments from this lead and every other lead they left here. The lead stays as an anonymous record. This can't be undone.`)) return;
    try {
      const { data } = await api.post(`/leads/${lead.id}/purge`);
      toast.success(`Shopper data removed from ${data.leads_scrubbed} lead${data.leads_scrubbed === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Leads</h1>
        <p className="text-sm text-slate-400 mt-1">
          Contact requests captured from QR scans, lead walls &amp; the specialist matcher.
        </p>
      </div>

      <WallInsightsPanel />

      {stores.length > 0 && leads.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="leads-store-filter">
          <Building2 className="h-4 w-4 text-slate-400" />
          {[["", "All stores", leads.length], ...stores.map((s) => [s.id, s.name, leads.filter((l) => l.store_id === s.id).length]),
            ["unassigned", "Unassigned", leads.filter((l) => !l.store_id && l.vehicle_id).length]].map(([key, label, n]) => (
            <Button key={key || "all"} variant="outline" size="sm" onClick={() => setStoreFilter(key)} data-testid={`leads-store-${key || "all"}`} aria-pressed={storeFilter === key}
              className={storeFilter === key ? "h-8 border-[#0A8CF2] bg-[#0A8CF2]/15 text-[#7FC3FF] hover:bg-[#0A8CF2]/25 font-semibold" : "h-8 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"}>
              {label} ({n})
            </Button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-24 rounded-xl border border-dashed border-[#2A2E3D]">
          <Inbox className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No leads yet</p>
          <p className="text-sm text-slate-500 mt-1">
            When a shopper requests contact, it lands here.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {shown.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-10" data-testid="leads-store-empty">No leads for this store yet.</p>
          )}
          {shown.map((l) => (
            <div
              key={l.id}
              data-testid={`lead-${l.id}`}
              className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5"
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <User className="h-4 w-4 text-[#0A8CF2]" />
                    <span className={`font-semibold ${l.pii_purged_at ? "text-slate-500 italic" : ""}`}>{l.customer_name}</span>
                    {l.pii_purged_at && (
                      <Badge variant="outline" data-testid={`lead-purged-${l.id}`} title={`Contact details removed ${new Date(l.pii_purged_at).toLocaleDateString()} (${l.pii_purge_reason === "retention" ? "retention window" : "dealer request"})`} className="border-slate-700 bg-slate-900 text-slate-400 text-[10px] flex items-center gap-1">
                        <Eraser className="h-2.5 w-2.5" /> Details purged
                      </Badge>
                    )}
                    <Badge variant="outline" className={STATUS_STYLES[l.status] || ""}>
                      {l.status}
                    </Badge>
                    <Badge
                      variant="outline"
                      data-testid={`lead-crm-status-${l.id}`}
                      title={l.crm_error || ""}
                      className={`${CRM_STYLES[l.crm_status] || CRM_STYLES.pending} font-mono-tech text-[10px] uppercase`}
                    >
                      CRM · {l.crm_status || "pending"}
                    </Badge>
                    <Badge variant="outline" data-testid={`lead-source-${l.id}`} className="border-slate-700 bg-slate-900 text-slate-300 text-[10px]">
                      {SOURCE_LABELS[l.source] || SOURCE_LABELS.matcher}
                    </Badge>
                    {l.store_name ? (
                      <Badge variant="outline" data-testid={`lead-store-${l.id}`} className="border-[#0A8CF2]/50 bg-[#0A8CF2]/10 text-[#7FC3FF] text-[10px] flex items-center gap-1">
                        <Building2 className="h-2.5 w-2.5" /> {l.store_name}
                      </Badge>
                    ) : hasStores && l.vehicle_id && l.crm_status !== "pending" ? (
                      <Badge variant="outline" data-testid={`lead-store-${l.id}`} title="This unit matched no store rule — sent to the group default inbox" className="border-amber-500/60 bg-amber-950/40 text-amber-300 text-[10px] flex items-center gap-1">
                        <Building2 className="h-2.5 w-2.5" /> Store unassigned
                      </Badge>
                    ) : null}
                    {l.contact_requested && (
                      <Badge variant="outline" data-testid={`lead-contact-requested-${l.id}`} className="border-[#0A8CF2]/50 bg-[#0A8CF2]/10 text-[#7FC3FF] text-[10px]">
                        Requested a call
                      </Badge>
                    )}
                    {l.unlock && (
                      <Badge variant="outline" data-testid={`lead-unlock-${l.id}`} className="border-emerald-700/50 bg-emerald-950/40 text-emerald-400 text-[10px] flex items-center gap-1">
                        <Unlock className="h-2.5 w-2.5" /> {UNLOCK_LABELS[l.unlock] || "Unlocked"}{l.unlock === "lower_price" && l.unlock_price != null ? ` · ${money(l.unlock_price)}` : ""}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-400">
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 hover:text-white">
                        <Phone className="h-3.5 w-3.5" /> {l.phone}
                      </a>
                    )}
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-white">
                        <Mail className="h-3.5 w-3.5" /> {l.email}
                      </a>
                    )}
                  </div>
                  {l.vehicle_label && (
                    <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-300">
                      <Car className="h-3.5 w-3.5 text-slate-500" /> {l.vehicle_label}
                    </div>
                  )}
                  {l.matched_specialist_name && (
                    <div className="flex items-center gap-1.5 mt-1 text-sm text-[#7FC3FF]">
                      <Sparkles className="h-3.5 w-3.5" /> Matched: {l.matched_specialist_name}
                    </div>
                  )}
                  {l.customer_style?.label && (
                    <div className="text-xs text-slate-400 mt-1" data-testid={`lead-style-${l.id}`}>
                      Style: <span className="text-slate-200 font-medium">{l.customer_style.label}</span>
                    </div>
                  )}
                  {l.match_reason && (
                    <p className="text-xs text-slate-500 mt-1 italic">“{l.match_reason}”</p>
                  )}
                  {l.crm_status === "failed" && l.crm_error && (
                    <p className="text-xs text-red-400 mt-1" data-testid={`lead-crm-error-${l.id}`}>CRM: {l.crm_error}</p>
                  )}
                  {l.message && (
                    <p className="text-sm text-slate-400 mt-2 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
                      {l.message}
                    </p>
                  )}
                  {l.comment && (
                    <div className="mt-2 rounded-lg border border-slate-800 bg-slate-950 px-3 py-2" data-testid={`lead-comment-${l.id}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-1"><MessageSquareQuote className="h-3 w-3" /> Shopper's comment</div>
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{l.comment}</p>
                    </div>
                  )}
                  {l.comment_summary && (
                    <div className="mt-2 rounded-lg border border-[#0A8CF2]/30 bg-[#0A8CF2]/5 px-3 py-2" data-testid={`lead-summary-${l.id}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#7FC3FF] font-mono-tech mb-1"><Bot className="h-3 w-3" /> AI summary</div>
                      <p className="text-sm text-slate-200 whitespace-pre-wrap">{l.comment_summary}</p>
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                    <SelectTrigger
                      data-testid={`lead-status-${l.id}`}
                      className="w-36 bg-slate-950 border-slate-800 text-slate-200"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-slate-600 mt-2 text-right font-mono-tech">
                    {new Date(l.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2 mt-3 justify-end flex-wrap">
                    {isAdmin && !l.pii_purged_at && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => purge(l)}
                        data-testid={`lead-purge-${l.id}`}
                        title="Shopper asked to be forgotten: remove their contact details"
                        className="h-8 border-slate-700 bg-slate-900/50 hover:bg-red-950/60 hover:text-red-300 text-slate-400"
                      >
                        <Eraser className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAdfLead(l)}
                      data-testid={`lead-view-adf-${l.id}`}
                      className="h-8 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                    >
                      <FileCode2 className="mr-1.5 h-3.5 w-3.5" /> ADF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resend(l)}
                      disabled={resending === l.id || !!l.pii_purged_at}
                      data-testid={`lead-resend-crm-${l.id}`}
                      className="h-8 border-[#0A8CF2]/50 bg-[#0A8CF2]/10 hover:bg-[#0A8CF2]/25 text-[#7FC3FF]"
                    >
                      {resending === l.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <><Send className="mr-1.5 h-3.5 w-3.5" /> Resend</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <LeadAdfDialog lead={adfLead} onClose={() => setAdfLead(null)} />
    </div>
  );
}
